import { useState } from 'react';

const areas = [
  { id: '30A', label: '30A', icon: 'ri-road-map-line' },
  { id: 'Panama City Beach', label: 'Panama City Beach', icon: 'ri-sun-line' },
  { id: 'I don’t know yet', label: 'I don’t know yet', icon: 'ri-compass-3-line' },
];

const intents = [
  { id: 'multi-family', label: 'A multi-family trip', brief: 'a multi-family trip', icon: 'ri-team-line' },
  { id: 'beachfront', label: 'Feet-in-the-sand beachfront', brief: 'feet-in-the-sand beachfront', icon: 'ri-water-flash-line' },
  { id: 'private-pool', label: 'A pool', brief: 'a pool', icon: 'ri-drop-line' },
  { id: 'quiet-30a', label: 'A quiet 30A rhythm', brief: 'a quiet 30A rhythm', icon: 'ri-leaf-line' },
  { id: 'large-group', label: 'Best for a large group', brief: 'a strong fit for a large group', icon: 'ri-home-6-line' },
  { id: 'walkable', label: 'Walkable to town', brief: 'walkable shopping and dining', icon: 'ri-footprint-line' },
];

const groupSizes = ['1–4', '5–8', '9–12', '13–16', '17–20'];

function buildTripBrief(area: string, selectedIntents: string[], size: string) {
  const labels = intents
    .filter((item) => selectedIntents.includes(item.id))
    .map((item) => item.brief);

  const areaPhrase = area === 'I don’t know yet' ? "We're not sure on the area yet" : `We're looking in ${area}`;

  return `${areaPhrase} for ${size} guests and want ${labels.join(', ')}. Which property should we look at?`;
}

export default function GuidedBooking() {
  const [area, setArea] = useState<string | null>(null);
  const [selectedIntents, setSelectedIntents] = useState<string[]>([]);
  const [size, setSize] = useState<string | null>(null);

  const readyToHandoff = area && selectedIntents.length > 0 && size;

  const toggleIntent = (id: string) => {
    setSelectedIntents((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  };

  const continueWithGuide = () => {
    if (!readyToHandoff || !area || !size) return;
    window.dispatchEvent(
      new CustomEvent('inspire:trip-criteria', {
        detail: {
          message: buildTripBrief(area, selectedIntents, size),
          area,
          preferences: selectedIntents,
          groupSize: size,
        },
      }),
    );
    document.getElementById('concierge')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section id="guided" className="relative bg-background-50 py-20 md:py-32">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.24em] text-accent-700 mb-4">
              <span className="inline-block w-6 h-px bg-accent-500 align-middle mr-3" />
              A quick conversation
            </div>
            <h2 className="font-heading text-foreground-950 text-4xl md:text-6xl leading-[1.05] tracking-[-0.01em]">
              Choose an area, then tell us what matters most.
              <span className="block italic font-light text-foreground-600">
                We&rsquo;ll point you toward the stays that fit.
              </span>
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-8">
          <div className="lg:col-span-12 xl:col-span-4 bg-background-100/70 border border-background-200/70 rounded-2xl p-6 md:p-8">
            <p className="text-xs uppercase tracking-[0.2em] text-foreground-500 mb-5">
              01 · Where do you want to stay?
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-1 gap-3">
              {areas.map((item) => {
                const active = area === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setArea(item.id)}
                    className={`group flex items-center gap-4 px-5 py-4 rounded-xl border text-left cursor-pointer transition-all ${
                      active
                        ? 'bg-foreground-950 border-foreground-950 text-background-50'
                        : 'bg-[#EFE4D6] border-[#DED0BE] text-foreground-900 hover:border-[#B99F87]'
                    }`}
                  >
                    <span
                      className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                        active
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-secondary-100 text-foreground-800 group-hover:bg-accent-100 group-hover:text-accent-800'
                      }`}
                    >
                      <i className={`${item.icon} text-base`} />
                    </span>
                    <span className="font-heading text-base md:text-xl leading-tight">{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-12 xl:col-span-8 bg-background-100/70 border border-background-200/70 rounded-2xl p-6 md:p-10">
            <p className="text-xs uppercase tracking-[0.2em] text-foreground-500 mb-6">
              02 · What matters most for this stay?
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              {intents.map((i) => {
                const active = selectedIntents.includes(i.id);
                return (
                  <button
                    key={i.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleIntent(i.id)}
                    className={`group flex items-center gap-4 px-5 py-4 rounded-xl border text-left cursor-pointer transition-all ${
                      active
                        ? 'bg-foreground-950 border-foreground-950 text-background-50'
                        : 'bg-[#EFE4D6] border-[#DED0BE] text-foreground-900 hover:border-[#B99F87]'
                    }`}
                  >
                    <span
                      className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
                        active
                          ? 'bg-accent-500 text-background-50'
                          : 'bg-secondary-100 text-foreground-800 group-hover:bg-accent-100 group-hover:text-accent-800'
                      }`}
                    >
                      <i className={`${i.icon} text-base`} />
                    </span>
                    <span className="font-heading text-base md:text-xl leading-tight">{i.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-4 bg-background-100/70 border border-background-200/70 rounded-2xl p-6 md:p-8">
              <p className="text-xs uppercase tracking-[0.2em] text-foreground-500 mb-5">
                03 · How many guests?
              </p>
              <div className="flex flex-wrap gap-2">
                {groupSizes.map((g) => {
                  const active = size === g;
                  return (
                    <button
                      key={g}
                      type="button"
                      onClick={() => setSize(g)}
                      className={`px-4 py-2 rounded-lg border text-sm cursor-pointer whitespace-nowrap transition-all ${
                        active
                          ? 'bg-foreground-950 border-foreground-950 text-background-50'
                          : 'bg-[#EFE4D6] border-[#DED0BE] text-foreground-800 hover:border-[#B99F87]'
                      }`}
                    >
                      {g}
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              className={`lg:col-span-8 rounded-2xl p-6 md:p-8 border transition-all ${
                readyToHandoff
                  ? 'bg-accent-500 border-accent-500 text-background-50'
                  : 'bg-background-100/70 border-background-200/70 text-foreground-800'
              }`}
            >
              <p
                className={`text-xs uppercase tracking-[0.2em] mb-4 ${
                  readyToHandoff ? 'text-background-50/80' : 'text-foreground-500'
                }`}
              >
                Ready when you are
              </p>
              <p
                className={`font-heading text-2xl md:text-3xl leading-tight mb-6 ${
                  readyToHandoff ? 'text-background-50' : 'text-foreground-900'
                }`}
              >
                {readyToHandoff
                  ? 'Lovely. Let\u2019s find your property.'
                  : 'Choose an area, what matters most, and how many guests.'}
              </p>
              <button
                type="button"
                onClick={continueWithGuide}
                disabled={!readyToHandoff}
                className={`inline-flex items-center gap-2 px-5 py-3 rounded-lg text-sm cursor-pointer whitespace-nowrap transition-all ${
                  readyToHandoff
                    ? 'bg-[#EFE4D6] text-accent-800 hover:bg-[#E9DCCB]'
                    : 'bg-foreground-950 text-background-50 opacity-80 hover:bg-foreground-800'
                }`}
              >
                Continue with the Insider
                <i className="ri-arrow-right-up-line" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
